"use client";

import { IconAlertInfoOutline18, IconArrowUpRightOutline18, IconCircleCheckOutline18 } from "nucleo-ui-outline-18";
import { Input } from "@/components/ui/input";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { memo, useCallback, useEffect, useRef, useState } from "react";

interface PolymarketProfile {
  name: string;
  pseudonym: string;
  proxyWallet: string;
  profileImage?: string;
}

interface PolymarketUserInputProps {
  label: string;
  description?: string;
  headerRight?: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
}

function PolymarketUserInput({
  label,
  description,
  headerRight,
  value,
  onChange,
  placeholder = "e.g. gabagool22 or 0x...",
  required = false,
}: PolymarketUserInputProps) {
  const [isTouched, setTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [profile, setProfile] = useState<PolymarketProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const searchIdRef = useRef(0);

  const validateUsername = useCallback(async (input: string) => {
    if (!input || input.length < 2) {
      setProfile(null);
      setError(null);
      return;
    }

    const isAddr = /^0x[a-fA-F0-9]{40}$/.test(input);
    const currentId = ++searchIdRef.current;
    setIsLoading(true);
    setError(null);

    try {
      const query = isAddr ? `address=${encodeURIComponent(input)}` : `username=${encodeURIComponent(input)}`;
      const res = await fetch(`/api/polymarket/profile?${query}`);
      // Ignore stale responses
      if (currentId !== searchIdRef.current) return;

      if (res.ok) {
        const data = await res.json();
        setProfile(data.profile);
        setError(null);
      } else {
        setProfile(null);
        setError(res.status === 404 ? "User not found" : "Validation failed");
      }
    } catch {
      if (currentId !== searchIdRef.current) return;
      setProfile(null);
      setError("Validation failed");
    } finally {
      if (currentId === searchIdRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    setProfile(null);
    const timeoutId = setTimeout(() => validateUsername(value), 500);
    return () => clearTimeout(timeoutId);
  }, [value, validateUsername]);

  const isValid = !!profile;
  const showRequiredError = isTouched && !value && required;
  const showNotFoundError = isTouched && !!error && !!value && !isLoading;

  // Always use /@{value} — Polymarket resolves both usernames and addresses at this path
  const profileUrl = `https://polymarket.com/@${value}`;

  return (
    <div className="space-y-2">
      {label && (
        <div className="flex items-center justify-between gap-3">
          <label className="field-label block">
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>
          {headerRight}
        </div>
      )}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
      <div className="relative">
        <Input
          type="text"
          value={value || ""}
          onChange={e => onChange(e.target.value)}
          onBlur={() => setTouched(true)}
          placeholder={placeholder}
          className={`pr-10 ${
            showRequiredError || showNotFoundError
              ? "border-red-500 focus-visible:ring-red-500"
              : isValid && !isLoading
                ? "border-green-500 focus-visible:ring-green-500"
                : ""
          }`}
        />

        {/* Status icons */}
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
          {isLoading && <PixelLoader size="sm" color="blue" />}
          {!isLoading && (showRequiredError || showNotFoundError) && (
            <IconAlertInfoOutline18 className="h-4 w-4 text-red-500" />
          )}
          {!isLoading && isValid && <IconCircleCheckOutline18 className="h-4 w-4 text-green-500" />}
        </div>
      </div>
      {/* Resolved profile info */}
      {isValid && !isLoading && value && (
        <div className="text-ds-text-subtle dark:text-ds-text-disabled flex items-center justify-between gap-2 text-xs">
          <span className="truncate">
            {profile?.name ? (
              <>
                <span className="text-ds-text-primary font-medium dark:text-zinc-100">@{profile.name}</span>
                {profile.proxyWallet && (
                  <>
                    {" · "}
                    <span className="font-mono">
                      {profile.proxyWallet.slice(0, 6)}…{profile.proxyWallet.slice(-4)}
                    </span>
                  </>
                )}
              </>
            ) : (
              <span className="font-mono">
                {value.slice(0, 6)}…{value.slice(-4)}
              </span>
            )}
          </span>
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex flex-shrink-0 items-center gap-1 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            View <IconArrowUpRightOutline18 className="h-3 w-3" />
          </a>
        </div>
      )}
      {/* Error messages */}
      {showRequiredError && <p className="micro-label text-red-600 dark:text-red-400">Username is required</p>}
      {showNotFoundError && <p className="micro-label text-red-600 dark:text-red-400">{error}</p>}
      {/* Helper text */}
      {!value && <p className="meta-xs">Enter a Polymarket username (e.g. gabagool22) or wallet address (0x...)</p>}
    </div>
  );
}

export default memo(PolymarketUserInput);
